import { DocBase } from "../docBase.tsx";
import { NWDocText } from "../nw-doc-text";

export function getElementHeight<T>(element: HTMLElement | DocBase<T>): number {
  if (element instanceof DocBase) {
    return element.getElementHeight();
  }

  // Get the computed styles
  const styles = window.getComputedStyle(element);

  // Get the actual height including margins
  const height = element.offsetHeight;
  const marginTop = parseFloat(styles.marginTop) || 0;
  const marginBottom = parseFloat(styles.marginBottom) || 0;

  // Log the height calculation for debugging
  console.log("Element height calculation:", {
    element: element.tagName,
    offsetHeight: height,
    marginTop,
    marginBottom,
    totalHeight: height + marginTop + marginBottom,
  });

  // Return the total height including margins
  return Math.ceil(height + marginTop + marginBottom);
}

/**
 * Creates a clone of an element with the same styling but empty content
 * to measure available space on a page
 */
export function createEmptyClone(element: HTMLElement): HTMLElement {
  const clone = element.cloneNode(true) as HTMLElement;
  clone.innerHTML = "";
  return clone;
}

/**
 * Measures the available height on a page by creating a clone of the page
 * and measuring how much space is left
 */
export function measureAvailableHeight(page: HTMLElement): number {
  const pageHeight = page.offsetHeight;
  const contentHeight = Array.from(page.children).reduce((total, child) => {
    return total + getElementHeight(child as HTMLElement);
  }, 0);

  return pageHeight - contentHeight;
}

/**
 * Checks if an element can fit on a page with the given available height
 */
export function canElementFit(element: HTMLElement, availableHeight: number): boolean {
  return getElementHeight(element) <= availableHeight;
}

/**
 * Splits text content into two parts based on the available height
 * @param textElement The text element to split
 * @param availableHeight The available height on the current page
 * @returns An object containing the first part and second part of the split text
 */
export function splitTextContent(textElement: HTMLElement, availableHeight: number) {
  const originalContent = (textElement as NWDocText).getText() || "";
  return trimTextToY(originalContent, availableHeight);
}

/**
 * Trims text content to fit within a specified height
 * @param text The original text content to trim
 * @param maxY The maximum height available for the text
 * @returns An object containing the trimmed text and the remaining text
 */
function trimTextToY(text: string, maxY: number): { trimmed: string; remaining: string } {
  const sentences: string[] = text.split(". ");

  let currentY: number = 0;
  const lineHeight: number = 28;
  const result: string[] = [];
  const remaining: string[] = [];
  let splitIndex: number = -1;

  for (let i = 0; i < sentences.length; i++) {
    const sentence = sentences[i];
    const formattedSentence = sentence + (i !== sentences.length - 1 ? "." : "");

    const words: string[] = formattedSentence.split(" ");
    let line: string = "";
    let numLines: number = 1;

    for (const word of words) {
      const testLine: string = line ? line + " " + word : word;
      if (testLine.length > 80) {
        // Assuming ~80 chars per line
        numLines++;
        line = word;
      } else {
        line = testLine;
      }
    }

    // Calculate total height needed for this sentence
    const sentenceHeight: number = numLines * lineHeight;

    // Check if adding this sentence would exceed maxY
    if (currentY + sentenceHeight <= maxY) {
      result.push(formattedSentence);
      currentY += sentenceHeight;
    } else {
      splitIndex = i;
      break;
    }
  }

  if (splitIndex !== -1) {
    for (let i = splitIndex; i < sentences.length; i++) {
      const formattedSentence = sentences[i] + (i !== sentences.length - 1 ? "." : "");
      remaining.push(formattedSentence);
    }
  }

  return {
    trimmed: result.join(" "),
    remaining: remaining.join(" "),
  };
}
